import type { WalkthroughStep } from "../types";

/**
 * Stream Budget's tour, started by the demo button or by asking for it.
 *
 * The pack behind it is the collision pair fed through a folder pick, which is
 * the only gesture whose duplicate pass can run: a zip's binary entry is carried
 * at its exact size with no body, so there is nothing to sign. That is why the
 * duplicate step below can point at a real finding rather than describing one.
 *
 * The `welcome` step announces the sample and is dropped by the island when no
 * sample is loaded, because "not your files" is false over a folder the reader
 * picked themselves.
 *
 * Also reachable without loading a sample. The island renders a permanent
 * "Show me how this works" control in its intake row (`ShowTourButton`), which
 * raises the same signal, because gating every route to the tour on the demo
 * button left the tool's actual user — somebody who dropped their own folder —
 * with no tutorial and no way to ask for one. So a step may still assume a
 * result is on screen, and may no longer assume that result came from us:
 * anything that describes the sample by name says so explicitly rather than
 * relying on there being no other way in.
 */
export const steps: WalkthroughStep[] = [
  {
    id: "welcome",
    title: "This is a sample pack",
    body: "Two parts packs shipping one 5 MB wheel between them are loaded — not your files. Five stops, then drop your own folder.",
    placement: "center",
  },
  {
    id: "drop",
    title: "Where your folder goes",
    body: "The first pass reads sizes only. A zip is listed from its central directory without unpacking it, and a folder pick never loads a file into memory.",
    selector: "[data-walkthrough='stb-drop']",
    placement: "bottom",
  },
  {
    id: "compare",
    title: "The second pass is a choice",
    body: "Duplicates are found by reading the first bytes of files that share a size. Switch it off and the report says the comparison did not run — it never says there are no duplicates.",
    selector: "[data-walkthrough='stb-compare']",
    placement: "bottom",
  },
  {
    id: "totals",
    title: "What the headline number is",
    body: "Size on disk for every file in the drop, including the ones no client ever downloads. The caption under the tiles says so, because a total that is quietly something else is the whole problem with this measurement.",
    selector: "[data-walkthrough='stb-totals']",
    placement: "bottom",
  },
  {
    id: "buckets",
    title: "Where the bytes went",
    body: "Six buckets, every file in exactly one, adding up to the figure above. A large Other is not an error — it is source art and backups left inside a resource, downloaded by every client and read by nothing.",
    selector: "[data-walkthrough='stb-buckets']",
    placement: "top",
  },
  {
    id: "findings",
    title: "Duplicates, and what was not checked",
    body: "The same wheel in both packs is one download charged twice to every player who joins. Everything the passes skipped is listed here in words, beside the findings, and rides into every export.",
    selector: "[data-walkthrough='stb-findings']",
    placement: "top",
  },
];

export const finishLabel = "Drop my own folder";
